import React, { type ReactNode } from 'react';
import { styled as styled2 } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntlV2 as useIntl, FormattedMessage } from '@atlassian/jira-intl';
import type { Props } from './types';

const Bold = ({ children }: { children: ReactNode }) => (
	<Text size="large" as="strong">
		{children}
	</Text>
);
export const LeftImageBenefitToConnect = ({ renderImage, title, description, header }: Props) => {
	const { formatMessage } = useIntl();
	const isHeader = !!(header !== null && header);

	return (
		<>
			<ValueImageContainer>{renderImage}</ValueImageContainer>

			<ValueInfoContainer>
				<ValueTitle>{formatMessage(title)}</ValueTitle>

				{isHeader ? (
					<ValueDescriptionContainer>
						<FormattedMessage
							{...description}
							values={{
								bold: (chunks: ReactNode[]) => <Bold>{chunks}</Bold>,
							}}
						/>
					</ValueDescriptionContainer>
				) : (
					<>
						{formatMessage(description, {
							bold: (chunks: ReactNode[]) => <Bold>{chunks}</Bold>,
						})}
					</>
				)}
			</ValueInfoContainer>
		</>
	);
};

export const RightImageBenefitToConnect = ({ renderImage, title, description }: Props) => {
	const { formatMessage } = useIntl();

	return (
		<>
			<ValueInfoContainer>
				<ValueTitle>{formatMessage(title)}</ValueTitle>

				<ValueDescriptionContainer>
					{formatMessage(description, {
						bold: (chunks: React.ReactNode[]) => <Bold>{chunks}</Bold>,
					})}
				</ValueDescriptionContainer>
			</ValueInfoContainer>

			<ValueImageContainer>{renderImage}</ValueImageContainer>
		</>
	);
};

export const BenefitToConnect = ({
	renderImage,
	title,
	description,
	header = false,
	imagePosition = 'left',
}: Props) => (
	<div>
		<ValueContainer>
			{imagePosition === 'left' ? (
				<LeftImageBenefitToConnect
					renderImage={renderImage}
					title={title}
					description={description}
					header={header}
				/>
			) : (
				<RightImageBenefitToConnect
					renderImage={renderImage}
					title={title}
					description={description}
				/>
			)}
		</ValueContainer>
	</div>
);
export default BenefitToConnect;

const ValueTitle = (props: { children: React.ReactNode }) => (
	<Heading size="medium">
		<ValueTitleContent {...props} />
	</Heading>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ValueDescriptionContainer = styled2.div({
	marginRight: token('space.500', '40px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ValueContainer = styled2.div({
	display: 'flex',
	margin: `${token('space.400', '32px')} 0`,
	alignItems: 'center',
	height: '240px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ValueImageContainer = styled2.div({
	alignSelf: 'flex-start',
	display: 'flex',
	flex: '1 50%',
	flexShrink: 0,
	maxWidth: '50%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ValueInfoContainer = styled2.div({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 50%',
	flexShrink: 0,
	maxWidth: '50%',
	alignSelf: 'flex-start',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const visibleForTesting = { ValueContainer, ValueImageContainer, ValueInfoContainer };

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ValueTitleContent = styled2.div({
	marginBottom: token('space.200', '16px'),
	marginTop: token('space.200', '16px'),
	marginRight: token('space.600', '48px'),
});
